import { Head } from "@inertiajs/react";
import { useState } from "react";
import { salonpageR2ImageUrl, salonpageStaffVideoUrl, salonpageVideoMp4Url, encodeSalonpagePathId } from "@/lib/_url";
import { useVideoPlayer } from "@/lib/_use-video-player";
import { SalonpageFooter } from "./_footer";

interface Salon {
  id: string;
  name: string;
  nameKana: string;
}

interface StaffItem {
  id: string;
  name: string;
  kana: string;
  role: string;
  history: string;
  description: string;
  imageUrl: string;
  reserveUrl: string;
  hasWebReservation?: boolean | number | null;
  position: number;
  profileVideoId?: string | null;
  profileVideoOrientation?: string | null;
}

interface Meta {
  title: string;
  description: string;
}

interface Props {
  salon?: Salon;
  staff?: StaffItem[];
  meta?: Meta;
}

export default function SalonStaffIndex(props: Props) {
  if (!props.salon) return <NotFound label="Salon not found." />;

  const salon = props.salon;
  const staff = props.staff ?? [];
  const meta = props.meta ?? { title: `${salon.name} スタッフ`, description: `${salon.name}のスタッフ一覧です。` };

  return (
    <>
      <Head title={meta.title}>
        <meta name="description" content={meta.description} />
      </Head>

      <div className="min-h-screen bg-gray-100 text-zinc-950 antialiased">
        <main className="mx-auto min-h-screen max-w-[448px] rounded-b-[22px] bg-white shadow-[0_0_24px_rgba(15,23,42,0.06)]">
          <header className="sticky top-0 z-10 border-b border-gray-100 bg-white/95 px-4 py-3 backdrop-blur">
            <a href={`/salons/${encodeSalonpagePathId(salon.id)}#staff`} className="text-xs font-bold text-zinc-400">
              ← サロンへ戻る
            </a>
            <h1 className="mt-2 text-xl font-bold tracking-tight">スタッフ</h1>
            <p className="mt-1 text-sm text-zinc-500">{salon.name}</p>
          </header>

          <div className="grid grid-cols-2 gap-3 p-4">
            {staff.map((member) => (
              <a key={member.id} href={staffDetailUrl(member)} className="block">
                <StaffCardMedia salon={salon} member={member} />
                <h2 className="mt-2 line-clamp-2 text-sm font-bold leading-5">{member.name}</h2>
                <p className="mt-1 line-clamp-1 text-xs font-medium text-zinc-400">{member.history || member.role}</p>
                {member.description && <p className="mt-1 line-clamp-2 text-xs leading-5 text-zinc-500">{member.description}</p>}
              </a>
            ))}
          </div>

        </main>
        <SalonpageFooter salon={salon} current="staff" />
      </div>
    </>
  );
}

function NotFound({ label }: { label: string }) {
  return (
    <main className="grid min-h-screen place-items-center bg-gray-100 px-6 text-zinc-900">
      <p className="text-sm text-zinc-400">{label}</p>
    </main>
  );
}

function staffDetailUrl(staff: StaffItem) {
  return `/staffs/${encodeSalonpagePathId(staff.id)}`;
}

function StaffCardMedia({ salon, member }: { salon: Salon; member: StaffItem }) {
  const posterUrl = salonpageR2ImageUrl(salon.id, member.id, "thumbnail");
  const videoUrl = member.profileVideoId ? salonpageVideoMp4Url(member.profileVideoId) : salonpageStaffVideoUrl(salon.id, member.id);

  return (
    <StaffCardVideo
      videoUrl={videoUrl}
      posterUrl={posterUrl}
      alt={member.name}
    />
  );
}

function StaffCardVideo({ videoUrl, posterUrl, alt }: { videoUrl: string; posterUrl: string; alt: string }) {
  const [hasError, setHasError] = useState(false);
  const { videoRef } = useVideoPlayer({
    src: hasError ? null : videoUrl,
    poster: posterUrl,
    muted: true,
    loop: true,
  });

  if (hasError) {
    return <img src={posterUrl} alt={alt} className="aspect-[9/16] w-full rounded-md bg-gray-100 object-cover object-top" />;
  }

  return (
    <div className="aspect-[9/16] overflow-hidden rounded-md bg-gray-100">
      <video
        ref={videoRef}
        aria-label={alt}
        playsInline
        muted
        loop
        preload="metadata"
        poster={posterUrl}
        onError={() => setHasError(true)}
        className="h-full w-full object-cover object-top"
      />
    </div>
  );
}
